import html2canvas from "html2canvas";

// Every certificate modal renders its artwork inside this id, so the capture
// helpers can find it without each component passing a ref around.
export const CERTIFICATE_ELEMENT_ID = "certificateContainer";

// The artwork is authored at this size. Normalising every export to it keeps a
// PNG and a PDF of the same certificate identical, whatever the viewport was.
const EXPORT_WIDTH = 1968;
const EXPORT_HEIGHT = 1483;

// A4 landscape width in points. The page keeps that width but takes its height
// from the artwork's own ratio, so the certificate covers the page edge to
// edge - a true A4 page is wider than the artwork and left white bars down
// both sides. Printers scale it to fit the paper.
const PDF_PAGE_WIDTH = 841.89;
const PDF_ORIENTATION = "landscape";

export const DOWNLOAD_FORMATS = {
  image: "image",
  pdf: "pdf",
};

const nextFrame = () =>
  new Promise((resolve) => window.requestAnimationFrame(() => resolve()));

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// The card-level buttons open the modal and export in one gesture, so the node
// we need is usually still being mounted when we get here.
const waitForCertificateElement = async (timeout = 5000) => {
  const deadline = Date.now() + timeout;

  while (Date.now() < deadline) {
    const element = document.getElementById(CERTIFICATE_ELEMENT_ID);
    if (element && element.offsetWidth > 0) return element;
    await wait(60);
  }

  return null;
};

// html2canvas paints whatever is on screen at that instant - a half-decoded
// background or an unswapped webfont both end up baked into the download.
const waitForContent = async (element) => {
  const images = Array.from(element.querySelectorAll("img"));

  await Promise.all(
    images.map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise((resolve) => {
            img.addEventListener("load", resolve, { once: true });
            img.addEventListener("error", resolve, { once: true });
          })
    )
  );

  if (document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch (error) {
      // Font loading is best-effort; a missing swap is not worth failing over.
    }
  }

  // The name and date only render once the artwork reports loaded, so give
  // React a chance to commit that state before we snapshot.
  await nextFrame();
  await nextFrame();
};

const renderCertificateCanvas = async () => {
  const element = await waitForCertificateElement();

  if (!element) {
    throw new Error("Certificate element not found for download.");
  }

  await waitForContent(element);

  const canvas = await html2canvas(element, {
    scale: 4, // Ensure high resolution
    useCORS: true, // Enable cross-origin images
  });

  // Redraw at the fixed export size so the output never depends on the
  // viewport the user happened to be on.
  const resizedCanvas = document.createElement("canvas");
  resizedCanvas.width = EXPORT_WIDTH;
  resizedCanvas.height = EXPORT_HEIGHT;

  const context = resizedCanvas.getContext("2d");
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(
    canvas,
    0,
    0,
    canvas.width,
    canvas.height,
    0,
    0,
    resizedCanvas.width,
    resizedCanvas.height
  );

  return resizedCanvas;
};

const triggerDownload = (href, fileName) => {
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName;
  // Firefox ignores clicks on links that are not in the document.
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const safeFileName = (name) =>
  String(name || "certificate")
    .trim()
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, "_") || "certificate";

export const downloadCertificateImage = async (name) => {
  const canvas = await renderCertificateCanvas();
  triggerDownload(
    canvas.toDataURL("image/png"),
    `${safeFileName(name)}_certificate.png`
  );
};

export const downloadCertificatePdf = async (name) => {
  const canvas = await renderCertificateCanvas();
  // jsPDF is only needed once someone actually asks for a PDF - keep it out of
  // the initial bundle.
  const { jsPDF } = await import("jspdf");

  // Match the page to the artwork's ratio so there is nothing left over to
  // show through as a margin.
  const pageWidth = PDF_PAGE_WIDTH;
  const pageHeight = (PDF_PAGE_WIDTH * canvas.height) / canvas.width;

  const pdf = new jsPDF({
    orientation: PDF_ORIENTATION,
    unit: "pt",
    format: [pageWidth, pageHeight],
    compress: true,
  });

  pdf.setProperties({
    title: `${name || "Certificate"} - Certificate`,
    creator: "Alpha Capital",
  });

  // Drawn at the full page size, from the origin - the ratios match, so this
  // fills the page without distorting the artwork.
  // JPEG keeps a full-page photographic certificate to a sensible file size;
  // PNG of the same canvas runs to several megabytes.
  pdf.addImage(
    canvas.toDataURL("image/jpeg", 0.95),
    "JPEG",
    0,
    0,
    pageWidth,
    pageHeight
  );

  pdf.save(`${safeFileName(name)}_certificate.pdf`);
};

export const downloadCertificate = (format, name) =>
  format === DOWNLOAD_FORMATS.pdf
    ? downloadCertificatePdf(name)
    : downloadCertificateImage(name);
